import React, { useEffect, useState, useContext } from "react";
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
} from "@mui/material";
import { UserContext } from "../context/UserContext";
import API_BASE_URL from "../config";

const Notifications = () => {
  const { user } = useContext(UserContext);
  const [notifications, setNotifications] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchNotifications() {
      try {
        setLoading(true);

        // Fetch notifications
        const [servicesNotifRes, classifiedsNotifRes] = await Promise.all([
          fetch(`${API_BASE_URL}/services/notifications/${user.id}`),
          fetch(
            `${API_BASE_URL}/classifieds/notifications/${user.id}`
          ),
        ]);

        if (!servicesNotifRes.ok || !classifiedsNotifRes.ok) {
          throw new Error("Failed to fetch notifications");
        }

        const [servicesNotifs, classifiedsNotifs] = await Promise.all([
          servicesNotifRes.json(),
          classifiedsNotifRes.json(),
        ]);

        // Add source info to notifications
        const allNotifications = [
          ...servicesNotifs.map((n) => ({ ...n, source: "services" })),
          ...classifiedsNotifs.map((n) => ({ ...n, source: "classifieds" })),
        ];

        // Fetch services + classifieds data
        const [servicesRes, classifiedsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/services`),
          fetch(`${API_BASE_URL}/classifieds`),
        ]);

        if (!servicesRes.ok || !classifiedsRes.ok) {
          throw new Error("Failed to fetch service/classified data");
        }

        const [servicesData, classifiedsData] = await Promise.all([
          servicesRes.json(),
          classifiedsRes.json(),
        ]);

        const servicesArray = [
          ...(servicesData?.services || servicesData?.data || []),
          ...(classifiedsData?.services || classifiedsData?.data || []),
        ];

        // Debug logs (optional)
        console.log("User ID:", user?.id);
        console.log("All Notifications:", allNotifications);
        console.log("Services Array:", servicesArray);

        // Filter only active notifications
        const filteredNotifications = allNotifications.filter(
          (notification) => notification.status === "active"
        );

        setNotifications(filteredNotifications);
        setServices(servicesArray);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      fetchNotifications();
    }
  }, [user]);

  const updateNotificationStatus = async (id, action, source) => {
    const endpoint =
      source === "classifieds"
        ? `${API_BASE_URL}/classifieds/notifications/update`
        : `${API_BASE_URL}/services/notifications/update`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ notification_id: id, action }),
      });

      if (!response.ok) {
        throw new Error("Failed to update notification status");
      }

      const result = await response.json();

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, status: action === "accepted" ? "Accepted" : "Rejected" }
            : n
        )
      );

      alert(
        `Notification ${
          action === "accepted" ? "approved" : "rejected"
        } successfully!`
      );
    } catch (err) {
      alert("Error updating notification: " + err.message);
    }
  };

  if (loading) return <p>Loading notifications...</p>;
  if (error) return <p>Error loading notifications: {error}</p>;

  return (
    <div style={{ mt:2, padding:"30px"}}>
      <Typography
        variant="h3"
        color="#42326e"
        fontWeight="bold"
        textAlign="center"
        mb={3}
        mt={4}
      >
        Notifications
      </Typography>
      <Grid container spacing={3} justifyContent="center">
        {notifications.length === 0 ? (
          <Typography>No notifications available.</Typography>
        ) : (
          notifications.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.id}>
              <Card
                sx={{
                  padding: "20px",
                  boxShadow: 3,
                  borderRadius: "10px",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: "200px",
                }}
              >
                <CardContent>
                  <Typography variant="h6">{item.message}</Typography>
                  {item.status && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        marginTop: "10px",
                        color:
                          item.status === "Accepted"
                            ? "green"
                            : item.status === "Rejected"
                            ? "red"
                            : item.status === "Active"
                            ? "blue"
                            : "black",
                        fontWeight: "bold",
                      }}
                    >
                      Status: {item.status}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", gap: "10px", marginTop: "15px" }}>
                    <Button
                      onClick={() =>
                        updateNotificationStatus(
                          item.id,
                          "accepted",
                          item.source
                        )
                      }
                      variant="contained"
                      color="success"
                      sx={{ width: "100px" }}
                    >
                      Approve
                    </Button>
                    <Button
                      onClick={() =>
                        updateNotificationStatus(
                          item.id,
                          "rejected",
                          item.source
                        )
                      }
                      variant="contained"
                      color="error"
                      sx={{ width: "100px" }}
                    >
                      Reject
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </div>
  );
};

export default Notifications;
